import { useState, useEffect, useCallback } from 'react'
import { useV2Api } from '../../../hooks/useV2Api'
import './V2QuoteExplorer.css'

const GROUP_OPTIONS = [
  { value: 'month', label: 'Monthly' },
  { value: 'week', label: 'Weekly' },
  { value: 'day', label: 'Daily' },
  { value: 'lane', label: 'Shipping Lane' },
  { value: 'provider', label: 'Provider' },
  { value: 'quoteType', label: 'Quote Type' },
  { value: 'containerType', label: 'Container' },
  { value: 'status', label: 'Status' },
  { value: 'source', label: 'Source' }
]

const SORT_OPTIONS = {
  createdAt: 'Date',
  totalCost: 'Cost',
  transitDays: 'Transit',
  providerName: 'Provider',
  status: 'Status'
}

function defaultFilters() {
  const d = new Date()
  d.setMonth(d.getMonth() - 6)
  return {
    dateFrom: d.toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    groupBy: 'month',
    quoteType: '',
    originPort: '',
    destinationPort: '',
    providerId: '',
    status: '',
    minCost: '',
    maxCost: '',
    minTransitDays: '',
    maxTransitDays: '',
    containerType: '',
    commodity: '',
    source: '',
    sortBy: 'createdAt',
    sortDir: 'DESC',
    page: 1,
    limit: 20
  }
}

export default function V2QuoteExplorer() {
  const api = useV2Api()
  const [filters, setFilters] = useState(defaultFilters)
  const [options, setOptions] = useState(null)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [showFilters, setShowFilters] = useState(true)
  const [activeTab, setActiveTab] = useState('groups')

  // Load filter options
  useEffect(() => {
    api.getAnalyticsOptions().then(res => setOptions(res.data)).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = useCallback(async (overrides = {}) => {
    setLoading(true)
    setSearchError(null)
    try {
      const params = { ...filters, ...overrides }
      const clean = {}
      Object.entries(params).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null) clean[k] = v
      })
      const res = await api.queryQuoteAnalytics(clean)
      setResult(res.data)
    } catch (err) {
      // keep previous results on error
      setSearchError(err || new Error('Could not apply filters'))
    }
    setLoading(false)
  }, [filters, api])

  // Initial search
  useEffect(() => { doSearch() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value, page: key === 'page' ? value : 1 }
      // Origin/destination options depend on quoteType; clear stale selections
      // so a value that disappears from the dropdown isn't silently applied.
      if (key === 'quoteType') {
        next.originPort = ''
        next.destinationPort = ''
      }
      return next
    })
  }

  const handleSearch = () => {
    const updated = { ...filters, page: 1 }
    setFilters(updated)
    doSearch(updated)
  }

  const handleReset = () => {
    const d = defaultFilters()
    setFilters(d)
    doSearch(d)
  }

  const handlePageChange = (newPage) => {
    const updated = { ...filters, page: newPage }
    setFilters(updated)
    doSearch(updated)
  }

  const handleSort = (field) => {
    const newDir = filters.sortBy === field && filters.sortDir === 'DESC' ? 'ASC' : 'DESC'
    const updated = { ...filters, sortBy: field, sortDir: newDir, page: 1 }
    setFilters(updated)
    doSearch(updated)
  }

  const handleExport = async () => {
    try {
      const clean = {}
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null) clean[k] = v
      })
      const text = await api.exportQuoteAnalytics(clean)
      const blob = new Blob([text], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `quote-analytics-${filters.dateTo}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      // silent fail — user sees no download
    }
  }

  const sortIcon = (field) => {
    if (filters.sortBy !== field) return ''
    return filters.sortDir === 'ASC' ? ' \u2191' : ' \u2193'
  }

  const fmt = (val) => val == null ? '-' : '$' + Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 })
  const fmtFull = (val) => val == null ? '-' : '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const statusClass = (s) => `v2-qe-status v2-qe-status--${(s || '').toLowerCase()}`

  return (
    <div className="v2-qe">
      {/* Header */}
      <div className="v2-qe-header">
        <h1>Quote Analytics</h1>
        <div className="v2-qe-header-actions">
          <button className="v2-qe-btn v2-qe-btn--secondary v2-qe-btn--sm" onClick={handleExport}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filter Card */}
      <div className="v2-qe-card">
        <button className="v2-qe-filter-toggle" onClick={() => setShowFilters(f => !f)}>
          {showFilters ? '\u25BC' : '\u25B6'} Filters
        </button>

        {showFilters && (
          <div className="v2-qe-filters">
            {/* Row 1: Date + Group + Type */}
            <div className="v2-qe-filter-row">
              <div className="v2-qe-filter-group">
                <label>From</label>
                <input type="date" value={filters.dateFrom}
                  onChange={e => updateFilter('dateFrom', e.target.value)} />
              </div>
              <div className="v2-qe-filter-group">
                <label>To</label>
                <input type="date" value={filters.dateTo}
                  onChange={e => updateFilter('dateTo', e.target.value)} />
              </div>
              <div className="v2-qe-filter-group">
                <label>Group By</label>
                <select value={filters.groupBy} onChange={e => updateFilter('groupBy', e.target.value)}>
                  {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="v2-qe-filter-group">
                <label htmlFor="v2-qe-quote-type">Quote Type</label>
                <select id="v2-qe-quote-type" value={filters.quoteType} onChange={e => updateFilter('quoteType', e.target.value)}>
                  <option value="">All Types</option>
                  <option value="ocean_freight">Ocean Freight</option>
                  <option value="domestic_trucking">Domestic Trucking</option>
                </select>
              </div>
            </div>

            {/* Row 2: Lane + Provider + Status */}
            <div className="v2-qe-filter-row">
              <div className="v2-qe-filter-group">
                <label htmlFor="v2-qe-origin">Origin</label>
                <select id="v2-qe-origin" value={filters.originPort}
                  onChange={e => updateFilter('originPort', e.target.value)}>
                  <option value="">All Origins</option>
                  {options && [...new Set(
                    options.lanes
                      .filter(l => !filters.quoteType || l.quoteType === filters.quoteType)
                      .map(l => l.origin)
                  )].filter(Boolean).sort().map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="v2-qe-filter-group">
                <label htmlFor="v2-qe-destination">Destination</label>
                <select id="v2-qe-destination" value={filters.destinationPort}
                  onChange={e => updateFilter('destinationPort', e.target.value)}>
                  <option value="">All Destinations</option>
                  {options && [...new Set(
                    options.lanes
                      .filter(l => !filters.quoteType || l.quoteType === filters.quoteType)
                      .map(l => l.destination)
                  )].filter(Boolean).sort().map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="v2-qe-filter-group">
                <label>Provider</label>
                <select value={filters.providerId} onChange={e => updateFilter('providerId', e.target.value)}>
                  <option value="">All Providers</option>
                  {options && options.providers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="v2-qe-filter-group">
                <label>Status</label>
                <select value={filters.status} onChange={e => updateFilter('status', e.target.value)}>
                  <option value="">All</option>
                  {options && options.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Row 3: Cost + Transit + Container + Source */}
            <div className="v2-qe-filter-row">
              <div className="v2-qe-filter-group">
                <label>Min Cost ($)</label>
                <input type="number" placeholder="0" value={filters.minCost}
                  onChange={e => updateFilter('minCost', e.target.value)} />
              </div>
              <div className="v2-qe-filter-group">
                <label>Max Cost ($)</label>
                <input type="number" placeholder="Any" value={filters.maxCost}
                  onChange={e => updateFilter('maxCost', e.target.value)} />
              </div>
              <div className="v2-qe-filter-group">
                <label>Container</label>
                <select value={filters.containerType} onChange={e => updateFilter('containerType', e.target.value)}>
                  <option value="">All</option>
                  {options && options.containerTypes.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="v2-qe-filter-group">
                <label>Commodity</label>
                <input type="text" placeholder="Search..." value={filters.commodity}
                  onChange={e => updateFilter('commodity', e.target.value)} />
              </div>
            </div>

            <div className="v2-qe-filter-actions">
              <button className="v2-qe-btn v2-qe-btn--primary" onClick={handleSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Apply Filters'}
              </button>
              <button className="v2-qe-btn v2-qe-btn--secondary" onClick={handleReset}>Reset</button>
            </div>
            {searchError && (
              <div className="v2-qe-error" role="alert">
                <strong>Could not apply filters.</strong>
                <span>{searchError.message || 'The previous results are still shown.'}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary Metrics */}
      {result && (
        <div className="v2-qe-card">
          <div className="v2-qe-metrics">
            <div className="v2-qe-metric">
              <span className="v2-qe-metric-value">{result.summary.totalQuotes}</span>
              <span className="v2-qe-metric-label">Total Quotes</span>
            </div>
            <div className="v2-qe-metric">
              <span className="v2-qe-metric-value">{fmt(result.summary.avgCost)}</span>
              <span className="v2-qe-metric-label">Avg Cost</span>
            </div>
            <div className="v2-qe-metric">
              <span className="v2-qe-metric-value">{fmt(result.summary.minCost)}</span>
              <span className="v2-qe-metric-label">Min Cost</span>
            </div>
            <div className="v2-qe-metric">
              <span className="v2-qe-metric-value">{fmt(result.summary.maxCost)}</span>
              <span className="v2-qe-metric-label">Max Cost</span>
            </div>
            <div className="v2-qe-metric">
              <span className="v2-qe-metric-value">{fmt(result.summary.totalCostSum)}</span>
              <span className="v2-qe-metric-label">Total Spend</span>
            </div>
            <div className="v2-qe-metric">
              <span className="v2-qe-metric-value">{result.summary.avgTransitDays}d</span>
              <span className="v2-qe-metric-label">Avg Transit</span>
            </div>
            <div className="v2-qe-metric">
              <span className="v2-qe-metric-value">{result.summary.minTransitDays}-{result.summary.maxTransitDays}d</span>
              <span className="v2-qe-metric-label">Transit Range</span>
            </div>
          </div>

          {/* Breakdown chips */}
          {(Object.keys(result.summary.byQuoteType).length > 0 || Object.keys(result.summary.byStatus).length > 0) && (
            <div className="v2-qe-chips-row">
              {Object.keys(result.summary.byQuoteType).length > 0 && (
                <div className="v2-qe-chip-group">
                  <span className="v2-qe-chip-group-label">Type</span>
                  {Object.entries(result.summary.byQuoteType).map(([type, count]) => (
                    <span key={type} className="v2-qe-chip">
                      {type === 'ocean_freight' ? 'Ocean' : 'Trucking'}: {count}
                    </span>
                  ))}
                </div>
              )}
              {Object.keys(result.summary.byStatus).length > 0 && (
                <div className="v2-qe-chip-group">
                  <span className="v2-qe-chip-group-label">Status</span>
                  {Object.entries(result.summary.byStatus).map(([status, count]) => (
                    <span key={status} className="v2-qe-chip">{status}: {count}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      {result && (
        <div className="v2-qe-tabs">
          <button className={`v2-qe-tab${activeTab === 'groups' ? ' v2-qe-tab--active' : ''}`}
            onClick={() => setActiveTab('groups')}>
            Breakdown ({result.groups.length})
          </button>
          <button className={`v2-qe-tab${activeTab === 'quotes' ? ' v2-qe-tab--active' : ''}`}
            onClick={() => setActiveTab('quotes')}>
            Individual Quotes ({result.pagination.total})
          </button>
        </div>
      )}

      {/* Grouped Breakdown */}
      {result && activeTab === 'groups' && (
        <div className="v2-qe-card" style={{ borderTopLeftRadius: 0 }}>
          {result.groups.length > 0 ? (
            <div className="v2-qe-table-wrap">
              <table className="v2-qe-table">
                <thead>
                  <tr>
                    <th>{GROUP_OPTIONS.find(o => o.value === filters.groupBy)?.label || 'Group'}</th>
                    <th>Quotes</th>
                    <th>Avg Cost</th>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Total</th>
                    <th>Avg Transit</th>
                  </tr>
                </thead>
                <tbody>
                  {result.groups.map((g, i) => (
                    <tr key={i}>
                      <td className="v2-qe-strong">{g.label}</td>
                      <td>{g.count}</td>
                      <td className="v2-qe-cost">{fmtFull(g.avgCost)}</td>
                      <td>{fmtFull(g.minCost)}</td>
                      <td>{fmtFull(g.maxCost)}</td>
                      <td className="v2-qe-cost">{fmtFull(g.totalCost)}</td>
                      <td>{g.avgTransitDays}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="v2-qe-empty">No grouped data for these filters.</div>
          )}
        </div>
      )}

      {/* Individual Quotes */}
      {result && activeTab === 'quotes' && (
        <div className="v2-qe-card" style={{ borderTopLeftRadius: 0 }}>
          {result.quotes.length > 0 ? (
            <>
              <div className="v2-qe-table-wrap">
                <table className="v2-qe-table">
                  <thead>
                    <tr>
                      <th className="v2-qe-sortable" onClick={() => handleSort('createdAt')}>
                        Date{sortIcon('createdAt')}
                      </th>
                      <th className="v2-qe-sortable" onClick={() => handleSort('providerName')}>
                        Provider{sortIcon('providerName')}
                      </th>
                      <th>Type</th>
                      <th className="v2-qe-sortable" onClick={() => handleSort('totalCost')}>
                        Cost{sortIcon('totalCost')}
                      </th>
                      <th className="v2-qe-sortable" onClick={() => handleSort('transitDays')}>
                        Transit{sortIcon('transitDays')}
                      </th>
                      <th>Lane</th>
                      <th>Container</th>
                      <th>Commodity</th>
                      <th className="v2-qe-sortable" onClick={() => handleSort('status')}>
                        Status{sortIcon('status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.quotes.map(q => (
                      <tr key={q.id}>
                        <td>{q.createdAt ? new Date(q.createdAt).toLocaleDateString() : '-'}</td>
                        <td className="v2-qe-strong">{q.providerName}</td>
                        <td>{q.quoteType === 'ocean_freight' ? 'Ocean' : 'Trucking'}</td>
                        <td className="v2-qe-cost">{fmtFull(q.totalCost)}</td>
                        <td>{q.transitDays ? `${q.transitDays}d` : '-'}</td>
                        <td>{q.originPort && q.destinationPort ? `${q.originPort} \u2192 ${q.destinationPort}` : '-'}</td>
                        <td>{q.containerType || '-'}{q.containerCount > 1 ? ` \u00D7${q.containerCount}` : ''}</td>
                        <td className="v2-qe-commodity">{q.commodity || '-'}</td>
                        <td><span className={statusClass(q.status)}>{q.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {result.pagination.totalPages > 1 && (
                <div className="v2-qe-pagination">
                  <button className="v2-qe-btn v2-qe-btn--secondary v2-qe-btn--sm"
                    disabled={result.pagination.page <= 1}
                    onClick={() => handlePageChange(result.pagination.page - 1)}>
                    Previous
                  </button>
                  <span className="v2-qe-page-info">
                    Page {result.pagination.page} of {result.pagination.totalPages}
                    {' '}({result.pagination.total} quotes)
                  </span>
                  <button className="v2-qe-btn v2-qe-btn--secondary v2-qe-btn--sm"
                    disabled={result.pagination.page >= result.pagination.totalPages}
                    onClick={() => handlePageChange(result.pagination.page + 1)}>
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="v2-qe-empty">No quotes match the selected filters.</div>
          )}
        </div>
      )}

      {/* Loading state (initial) */}
      {loading && !result && (
        <div className="v2-qe-card">
          <div className="v2-qe-loading">
            <div className="v2-qe-loading-spinner" />
            <div>Loading quote data...</div>
          </div>
        </div>
      )}
    </div>
  )
}
